import { useEntityList } from '@mainstage/plugin-catalog-react';
import { PracticeShaperFilters } from '../types';
import { useEffect, useMemo, useState } from 'react';
import { OutputTypeFilter, OutputTypeFilterValue } from '../filters';
import { isEqual } from 'lodash';

export function useOutputTypeFilter(opts: {
  initialValue: OutputTypeFilterValue;
  allowedTypes?: string[];
}): {
  selectedType: OutputTypeFilterValue;
  setSelectedType: (types: OutputTypeFilterValue) => void;
} {
  const { initialValue, allowedTypes } = opts;
  const {
    filters,
    queryParameters: { generates },
    updateFilters,
  } = useEntityList<PracticeShaperFilters>();

  const contextValue = filters.generates?.value;

  const queryParam = useMemo(() => {
    if (generates === 'all') return 'all';
    else if (generates !== undefined) return generates as string;
    return undefined;
  }, [generates]);

  const [selectedType, setSelectedType] = useState<string>(
    queryParam ?? contextValue ?? initialValue,
  );

  // Update selectedType when filters changes from outside (example filter gets reset from parent component)
  useEffect(() => {
    setSelectedType(current => {
      // We check if outer value is actually before updating to avoid extra renders
      if (contextValue && !isEqual(current, contextValue)) return contextValue;
      // If outer and inner state already match, by returning the current state we "abort" the state update, this won't trigger a re-render
      return current;
    });
  }, [contextValue]);

  // Update state based on query parameter updates: this happens at initial page load and from
  // external updates to the page location.
  useEffect(() => {
    if (queryParam) {
      setSelectedType(queryParam);
    }
  }, [queryParam]);

  useEffect(() => {
    updateFilters({
      generates: new OutputTypeFilter(selectedType, allowedTypes),
    });
  }, [selectedType, allowedTypes, updateFilters]);

  return {
    selectedType,
    setSelectedType,
  };
}
